import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { rankCommands } from '@/lib/command-search';
import { readRecentCommandIds, recordRecentCommandId } from '@/lib/command-recents';
import { EDITOR_COMMANDS, formatKeybinding, hasOpenCommandOverlay, type EditorCommandContext } from '@/lib/editor-commands';
import { Command, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PALETTE_COMMANDS = EDITOR_COMMANDS.filter((command) => command.id !== 'global.open-command-palette');

export function CommandPalette({ context, compact = false }: { context: EditorCommandContext; compact?: boolean }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const returnFocus = useRef<HTMLElement | null>(null);
  const recent = useMemo(() => open ? readRecentCommandIds() : [], [open]);
  const results = useMemo(() => rankCommands(PALETTE_COMMANDS, query, context, recent), [context, query, recent]);
  const activeIndex = Math.min(highlighted, Math.max(0, results.length - 1));

  const changeOpen = useCallback((next: boolean): void => {
    if (next) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpen(next);
    if (!next) {
      setQuery('');
      setHighlighted(0);
      window.requestAnimationFrame(() => returnFocus.current?.isConnected && returnFocus.current.focus());
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey || event.key.toLowerCase() !== 'k') return;
      if (!open && hasOpenCommandOverlay(document)) return;
      event.preventDefault();
      event.stopPropagation();
      changeOpen(!open);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [changeOpen, open]);

  const run = (index: number): void => {
    const result = results[index];
    if (!result?.enabled) return;
    recordRecentCommandId(result.command.id);
    changeOpen(false);
    result.command.execute(context);
  };

  return (
    <>
      <Button variant="ghost" size={compact ? 'icon' : 'sm'} className={compact ? 'size-8' : 'text-muted-foreground'} onClick={() => changeOpen(true)} aria-label="Open command palette" title="Command palette (Cmd/Ctrl+K)">
        <Command className="size-4" />{compact ? null : <><span>Commands</span><kbd className="ml-1 rounded border px-1 text-[9px]">⌘K</kbd></>}
      </Button>
      <Dialog open={open} onOpenChange={changeOpen}>
        <DialogContent hideClose className="overflow-hidden p-0 sm:max-w-[620px]">
          <DialogTitle className="sr-only">Command palette</DialogTitle>
          <DialogDescription className="sr-only">Search navigation and available actions.</DialogDescription>
          <div className="flex items-center gap-2 border-b px-4">
            <Search className="size-4 text-muted-foreground" />
            <input
              autoFocus
              role="combobox"
              aria-label="Search commands"
              aria-controls="command-palette-results"
              aria-expanded="true"
              aria-activedescendant={results[activeIndex] ? `command-${results[activeIndex].command.id}` : undefined}
              value={query}
              onChange={(event) => { setQuery(event.target.value); setHighlighted(0); }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') { event.preventDefault(); setHighlighted(Math.min(results.length - 1, activeIndex + 1)); }
                if (event.key === 'ArrowUp') { event.preventDefault(); setHighlighted(Math.max(0, activeIndex - 1)); }
                if (event.key === 'Enter') { event.preventDefault(); run(activeIndex); }
              }}
              placeholder="Search commands…"
              className="h-12 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div id="command-palette-results" role="listbox" className="max-h-[420px] overflow-y-auto p-2">
            {results.length === 0 ? <p className="px-3 py-10 text-center text-xs text-muted-foreground">No commands match “{query}”.</p> : results.map((result, index) => {
              const binding = result.command.keybindings[0];
              return (
                <button
                  key={result.command.id}
                  id={`command-${result.command.id}`}
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index}
                  disabled={!result.enabled}
                  onMouseMove={() => setHighlighted(index)}
                  onClick={() => run(index)}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left ${activeIndex === index ? 'bg-secondary' : ''} disabled:opacity-45`}
                >
                  <div className="min-w-0 flex-1"><div className="text-xs font-medium">{result.command.label}</div><div className="truncate text-[10px] text-muted-foreground">{result.enabled ? result.command.description : result.command.disabledReason?.(context) ?? 'Unavailable in the current context.'}</div></div>
                  <span className="text-[9px] uppercase text-muted-foreground">{result.command.category}</span>
                  {binding ? <kbd className="rounded border px-1.5 py-0.5 text-[9px] text-muted-foreground">{formatKeybinding(binding)}</kbd> : null}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 border-t px-4 py-2 text-[9px] text-muted-foreground"><span>↑↓ Navigate</span><span>↵ Run</span><span>Esc Close</span></div>
        </DialogContent>
      </Dialog>
    </>
  );
}
